import { useState, useRef, useEffect } from "react";
import { Avatar } from "primereact/avatar";
import { TieredMenu } from "primereact/tieredmenu";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MenuIcons, MenuLabels, USER_DETAIL } from "../../utils/constants";
import { REIButton, REIDivider } from "../ui";
import {
  hasArchistarActive,
  hasRPDataActive,
  openArchistarLogin,
  openRPDataLogin,
  redirectToClassicSite,
  toPascalCase,
} from "../../utils/commonUtil";

const AppToolBar: React.FC = () => {
  const tieredMenuRef = useRef<TieredMenu>(null);
  const userDetails = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [activeMenu, setActiveMenu] = useState<MenuLabels>(MenuLabels.INVESTAR_SEARCH);
  const [menuOpen, setMenuOpen] = useState(false); // State to toggle the menu on mobile
  const navigate = useNavigate();
  const location = useLocation();
  const resourcesMenuRef = useRef<TieredMenu>(null);

  // Map routes to MenuLabels for easy activation based on current path
  const routeMap: { [key in MenuLabels]: string } = {
    [MenuLabels.INVESTAR_SEARCH]: "/",
    [MenuLabels.RP_DATA_PRICEFINDER]: "/rp-data",
    [MenuLabels.ARCHISTAR]: "/archistar",
    [MenuLabels.PROPERTY_ANALYSER]: "/property-analyser",
    [MenuLabels.PORTFOLIO_TRACKER]: "/portfolio-tracker",
    [MenuLabels.MY_ACCOUNT]: "/myaccount",
    [MenuLabels.RESOURCES]: "/resources",
    [MenuLabels.HOME]: "/",
  };

  const resourcesItems = [
    {
      label: "Investment Calculator",
      icon: "pi pi-calculator",
      command: () => {
        setActiveMenu(MenuLabels.RESOURCES);
        navigate("/investment-calculators");
      },
    },
    {
      label: "Depreciation",
      icon: "pi pi-question-circle",
      command: () => {
        setActiveMenu(MenuLabels.RESOURCES);
        navigate("/depreciation");
      },
    },
    {
      label: "Suburb Reports",
      icon: "pi pi-envelope",
      command: () => {
        setActiveMenu(MenuLabels.RESOURCES);
        navigate("/suburb-reports");
      },
    },
    {
      label: "Suburb Insights",
      icon: "pi pi-chart-line",
      command: () => {
        setActiveMenu(MenuLabels.RESOURCES);
        navigate("/seo");
      },
    },
  ];

  // Update active menu based on the current route
  useEffect(() => {
    const currentPath = location.pathname;
    const foundMenu = Object.keys(routeMap).find((key) => routeMap[key as MenuLabels] === currentPath) as
      | MenuLabels
      | undefined;

    if (foundMenu) {
      setActiveMenu(foundMenu);
    } else if (
      currentPath === "/suburb-reports" ||
      currentPath === "/depreciation" ||
      currentPath === "/investment-calculators" ||
      currentPath === "/seo"
    ) {
      setActiveMenu(MenuLabels.RESOURCES);
    } else if (currentPath === "/property-analyser/analysis-report" || currentPath === "/analyse-property") {
      setActiveMenu(MenuLabels.PROPERTY_ANALYSER);
    }
  }, [location.pathname]);

  // Logout function
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "https://info.realestateinvestar.com.au/pro-member-login?tier=1";
  };

  // Define submenu items separately to keep code DRY
  const submenuItems = [
    {
      label: "My Account",
      icon: "pi pi-users",
      command: () => navigate("/myaccount"),
    },
    { label: "Logout", icon: "pi pi-sign-out", command: handleLogout },
  ];

  // Opens the submenu
  const openSubMenu = (event: React.MouseEvent) => {
    tieredMenuRef.current?.toggle(event);
  };

  // Handles menu click and routing
  const handleMenuClick = (label: MenuLabels, e?: React.MouseEvent) => {
    if (menuOpen) {
      setMenuOpen(!menuOpen);
    }
    if (label === MenuLabels.RP_DATA_PRICEFINDER && hasRPDataActive()) {
      openRPDataLogin();
      return;
    }
    if (label === MenuLabels.ARCHISTAR && hasArchistarActive()) {
      openArchistarLogin();
      return;
    }
    setActiveMenu(label);
    if (label === MenuLabels.ARCHISTAR) {
      navigate("/archistar");
      return;
    }
    if (label === MenuLabels.RESOURCES) {
      openResourcesMenu(e!);
      return;
    }

    navigate(routeMap[label]);
  };

  // Reusable button rendering logic
  const renderMenuButton = (icon: string, label: MenuLabels) => (
    <REIButton
      icon={icon}
      label={label}
      aria-label="Filter"
      onClick={(e) => handleMenuClick(label, e)}
      text={activeMenu !== label}
      classNames={`shadow-none rounded-none border-solid border border-y-0 border-r-0 border-gray-300 h-14 ${
        activeMenu === label ? "" : "text-[var(--text-color-secondary)]"
      }`}
    />
  );

  // Toggles the menu visibility on mobile
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);

    if (menuOpen) {
      const toggleButton = document.querySelector(".pi-bars") as HTMLElement;
      const menuElement = document.querySelector(".layout-topbars-menu") as HTMLElement;

      if (toggleButton && menuElement) {
        const rect = toggleButton.getBoundingClientRect();
        menuElement.style.position = "absolute";
        menuElement.style.left = `${rect.left + 32}px`;
      }
    }
  };

  const openResourcesMenu = (event: React.MouseEvent) => {
    resourcesMenuRef.current?.toggle(event);
  };

  return (
    <div className="layout-topbar">
      <Link to="/" className="layout-topbar-logo">
        <img
          src="https://info.realestateinvestar.com.au/hubfs/Logos/REI%20and%20MRI/Resized/RE%20Investar-Logo-MRI_Colour%20web%20229x115px%20(1).png"
          alt="Logo"
          style={{ height: "80px", width: "150px" }}
        />
      </Link>

      {/* Hamburger Icon for Mobile */}
      <div className="block lg:hidden p-4 cursor-pointer" onClick={toggleMenu}>
        <REIButton icon="pi pi-bars" classNames="" onClick={() => {}} />
      </div>

      {/* Menu buttons for larger screens */}
      <div
        className={`layout-topbars-menu lg:mt-0 max-lg:mt-[290px] max-lg:bg-slate-900 lg:ml-0 lg:min-w-0 lg:flex-1 lg:bg-transparent ${
          menuOpen ? "block" : "hidden"
        } lg:block`}
      >
        <div className="w-full flex flex-col lg:flex-row ">
          {renderMenuButton(MenuIcons.INVESTAR_SEARCH, MenuLabels.INVESTAR_SEARCH)}
          {renderMenuButton(MenuIcons.RP_DATA_PRICEFINDER, MenuLabels.RP_DATA_PRICEFINDER)}
          {renderMenuButton(MenuIcons.RP_DATA_PRICEFINDER, MenuLabels.ARCHISTAR)}
          {renderMenuButton(MenuIcons.PROPERTY_ANALYSER, MenuLabels.PROPERTY_ANALYSER)}
          {renderMenuButton(MenuIcons.PORTFOLIO_TRACKER, MenuLabels.PORTFOLIO_TRACKER)}
          {renderMenuButton(MenuIcons.RESOURCES, MenuLabels.RESOURCES)}
          {renderMenuButton(MenuIcons.MY_ACCOUNT, MenuLabels.MY_ACCOUNT)}
          <REIButton
            icon={"pi pi-sign-out"}
            label={"Logout"}
            aria-label="Filter"
            onClick={handleLogout}
            text={true}
            classNames={
              "shadow-none rounded-none border-solid border border-y-0 border-r-0 border-gray-300 h-12 text-[var(--text-color-secondary)] lg:hidden"
            }
          />
        </div>
      </div>

      <TieredMenu model={submenuItems} popup ref={tieredMenuRef} />
      <TieredMenu model={resourcesItems} popup ref={resourcesMenuRef} />
      <div className="layout-topbar-menu ml-auto hidden shrink-0 items-center lg:flex">
        <REIButton
          icon={"pi pi-history"}
          label={"Revert to Classic"}
          aria-label="Go to Classic Site"
          onClick={() => redirectToClassicSite()}
          text={true}
          classNames="ml-3 h-12 rounded-md border border-y-0 border-l-0 px-2 text-sm font-semibold text-[var(--text-color-secondary)] shadow-none hover:bg-[var(--surface-hover)] border-0 bg-cyan-600 hover:bg-cyan-600 text-white font-semibold rounded"
        />
        <REIDivider layout="vertical" className="mx-4" />
        <div className="flex items-center gap-3 border-l-[1px] border-l-gray-300">
        <div style={{ textAlign: "right" }}>
          <div>
            <span style={{ fontWeight: "bold", fontSize: "x-small" }}>
              {toPascalCase(userDetails.FirstName)} {toPascalCase(userDetails.LastName)}
            </span>
          </div>
        </div>
        <Avatar
          label={`${userDetails.FirstName?.[0] ?? ""}${userDetails.LastName?.[0] ?? ""}`.toUpperCase()}
          shape="circle"
          className="p-link layout-topbar-button"
          onClick={openSubMenu}
          style={{ backgroundColor: "rgb(66, 91, 118)", color: "#ffffff", minWidth: "40px" }}
        />
        </div>
      </div>
    </div>
  );
};

export default AppToolBar;
